import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useParams } from "react-router";
import { TRAINER_META } from "../../content/catalog";
import { gradeAnswer } from "../../domain/evaluate";
import { trainerFeedback } from "../../domain/feedback";
import { api } from "../api";
import {
  enqueueAttempt,
  listQueuedAttempts,
  loadLessonSnapshot,
  removeQueuedAttempt,
  saveLessonSnapshot,
  snapshotStillOpen,
} from "../offline/lesson";
import { PocketFrame, trainerEmoji } from "../shell";
import { FramePlayer } from "./FramePlayer";
import { PhrasePlayer } from "./PhrasePlayer";
import { SoundsPlayer } from "./SoundsPlayer";
import { wordEmoji } from "./SceneCard";
import { clearLastStep, readLastStep, writeLastStep, type ItemAnswerKey, type PlayItem } from "./types";

type SessionPayload = {
  id: string;
  items: PlayItem[];
  answerKeys?: Record<string, ItemAnswerKey>;
  lastItemIndex?: number;
  resumed?: boolean;
  startedAt?: string;
  entitlementExpiresAt?: string | null;
};

function networkFailed(err: unknown): boolean {
  return typeof err === "object" && err !== null && !("status" in err && (err as { status?: number }).status);
}

export function PlayPage({ extra }: { extra?: ReactNode }) {
  const { slug = "english-sounds" } = useParams();
  const loc = useLocation();
  const preview = loc.pathname.startsWith("/preview/");
  const meta = TRAINER_META.find((t) => t.slug === slug);
  const [items, setItems] = useState<PlayItem[]>([]);
  const [answerKeys, setAnswerKeys] = useState<Record<string, ItemAnswerKey>>({});
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const [msg, setMsg] = useState("");
  const [paywall, setPaywall] = useState(false);
  const [askResume, setAskResume] = useState(false);
  const [notice, setNotice] = useState("");
  const [done, setDone] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (preview) {
        if (!navigator.onLine) {
          setMsg("Этот материал ещё не загружен. Подключитесь к интернету.");
          return;
        }
        const res = await api<{ items: PlayItem[]; notice: string }>(`/api/v1/public/previews/${slug}`);
        if (cancelled) return;
        setItems(res.items);
        setNotice(res.notice);
        setI(0);
        return;
      }
      const applySnapshot = async () => {
        const snap = await loadLessonSnapshot(slug);
        if (!snap || !snapshotStillOpen(snap)) {
          setMsg(snap ? "Нужно подключиться, чтобы проверить срок доступа." : "Этот материал ещё не загружен. Подключитесь к интернету.");
          return false;
        }
        setSessionId(snap.sessionId);
        setItems(snap.items);
        setAnswerKeys(snap.answerKeys ?? {});
        setI(Math.min(snap.lastIndex, Math.max(0, snap.items.length - 1)));
        setNotice("Нет подключения к интернету. Можно продолжить загруженное занятие. Новые результаты сохранятся на этом устройстве.");
        setOffline(true);
        return true;
      };
      if (!navigator.onLine) {
        await applySnapshot();
        return;
      }
      try {
        await api("/api/v1/me");
        const store = await api<{ offers: Array<{ access_product_id: string; kind: string; trainer_slug?: string }> }>("/api/v1/public/storefront");
        const offer = store.offers.find((o) => o.trainer_slug === slug && o.kind === "one_time");
        const res = await api<SessionPayload>("/api/v1/learning/sessions", {
          method: "POST",
          body: JSON.stringify({ trainerSlug: slug, confirmTrial: true, accessProductId: offer?.access_product_id }),
        });
        if (cancelled) return;
        setSessionId(res.id);
        setItems(res.items);
        setAnswerKeys(res.answerKeys ?? {});
        const local = readLastStep(slug);
        const startAt = res.resumed ? (res.lastItemIndex ?? 0) : local?.sessionId === res.id ? local.index : res.lastItemIndex ?? 0;
        if (res.resumed && startAt > 0) setAskResume(true);
        setI(Math.min(startAt, Math.max(0, res.items.length - 1)));
        await saveLessonSnapshot({
          slug,
          sessionId: res.id,
          learnerKey: "active",
          items: res.items,
          answerKeys: res.answerKeys,
          startedAt: res.startedAt ? Date.parse(res.startedAt) : Date.now(),
          entitlementExpiresAt: res.entitlementExpiresAt ? Date.parse(res.entitlementExpiresAt) : null,
          lastIndex: startAt,
        });
        await flushOfflineQueue(res.id);
      } catch (e) {
        if ((e as { status?: number }).status === 402) setPaywall(true);
        else if (networkFailed(e) && (await applySnapshot())) return;
        else setMsg((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, preview]);

  useEffect(() => {
    if (offline || preview || !sessionId) return;
    void flushOfflineQueue(sessionId);
  }, [offline, preview, sessionId]);

  async function flushOfflineQueue(id: string) {
    const queued = await listQueuedAttempts(id);
    for (const row of queued) {
      try {
        await api("/api/v1/learning/attempts/batch", {
          method: "POST",
          body: JSON.stringify({
            sessionId: id,
            attempts: [{ id: row.id, itemId: row.itemId, selected: row.selected, clientSeq: row.clientSeq, support: "none" }],
          }),
        });
        await removeQueuedAttempt(row.id);
      } catch (err) {
        if (networkFailed(err)) {
          setNotice("Сохранено на устройстве. Отправим после подключения.");
          return;
        }
        throw err;
      }
    }
    if (queued.length) setNotice("Синхронизация завершена.");
  }

  async function submit(selected: unknown) {
    const item = items[i];
    if (!item) return;
    if (preview) {
      try {
        const res = await api<{ feedback: string }>(`/api/v1/public/previews/${slug}/grade`, {
          method: "POST",
          body: JSON.stringify({ itemId: item.id, selected }),
        });
        setMsg(res.feedback);
      } catch (err) {
        setMsg(networkFailed(err) ? "Нужно подключение, чтобы проверить ответ." : (err as Error).message);
        return;
      }
      if (i + 1 < items.length) setI(i + 1);
      else setDone(true);
      return;
    }
    if (!sessionId) return;
    const next = i + 1;
    const advance = async (feedback: string, completeOnline: boolean) => {
      setMsg(feedback);
      writeLastStep(slug, sessionId, next);
      const snap = await loadLessonSnapshot(slug);
      if (snap && snap.sessionId === sessionId) {
        await saveLessonSnapshot({ ...snap, lastIndex: next, completed: next >= items.length });
      }
      if (next < items.length) setI(next);
      else {
        if (completeOnline) {
          try {
            await api(`/api/v1/learning/sessions/${sessionId}/complete`, { method: "POST", body: "{}" });
          } catch (err) {
            if (!networkFailed(err)) throw err;
          }
        }
        clearLastStep(slug);
        setDone(true);
        setMsg("На сегодня всё.");
      }
    };
    try {
      const res = await api<{ results: Array<{ correct: boolean | null; feedback: string }>; lastItemIndex?: number }>(
        "/api/v1/learning/attempts/batch",
        {
          method: "POST",
          body: JSON.stringify({
            sessionId,
            attempts: [{ itemId: item.id, selected, clientSeq: i, support: "none" }],
          }),
        },
      );
      await advance(res.results[0]?.feedback ?? "", true);
    } catch (err) {
      if (!networkFailed(err)) {
        setMsg((err as Error).message);
        return;
      }
      const queued = await enqueueAttempt({
        sessionId,
        learnerKey: "active",
        itemId: item.id,
        selected,
        clientSeq: i,
      });
      if (!queued) {
        setMsg("Сохранено на устройстве. Отправим после подключения.");
        return;
      }
      const key = answerKeys[item.id];
      const correct = key ? gradeAnswer({ type: item.type, unscored: item.unscored, ...key }, selected) : null;
      setNotice("Пройденные шаги сохранены на этом устройстве и отправятся после подключения.");
      await advance(trainerFeedback(slug, item.type, correct), false);
    }
  }

  if (paywall) {
    return (
      <PocketFrame>
        <div className="page session">
          <h1>Пробный доступ закончился</h1>
          <p>Твой прогресс сохранён. Позови взрослого, чтобы выбрать доступ и продолжить с места остановки.</p>
          <a className="btn" href="/family/paywall">Позвать взрослого</a>
          <a className="btn ghost" href="/app/progress">Посмотреть прогресс</a>
        </div>
      </PocketFrame>
    );
  }

  const item = items[i];
  return (
    <PocketFrame>
    <div className="page session">
      <div className="session-copy">
      <p className="kicker">{preview ? "Знакомство с тренажёром" : `${meta?.title ?? "Занятие"} · шаг ${Math.min(i + 1, items.length || 1)} из ${items.length || 1}`}</p>
      <h1>{item?.prompt ?? (done ? "На сегодня всё." : "Загружаем занятие…")}</h1>
      {item?.word ? (
        <div className="word">
          <span aria-hidden="true">{wordEmoji(item.word)}</span>
          <strong>{item.word}</strong>
          {item.ipa ? <em>{item.ipa}</em> : null}
        </div>
      ) : meta ? (
        <p className="tile-emoji" aria-hidden="true">{trainerEmoji(meta.slug)}</p>
      ) : null}
      {notice || preview ? (
        <p className="muted">{notice || "Preview не запускает пробный период и не сохраняет учебный прогресс."}</p>
      ) : null}
      </div>
      {askResume ? (
        <div className="card">
          <p>Продолжить занятие? Мы сохранили последний завершённый шаг.</p>
          <div className="tools">
            <button type="button" className="btn" onClick={() => setAskResume(false)}>Продолжить</button>
            <button
              type="button"
              className="btn ghost"
              onClick={async () => {
                const store = await api<{ offers: Array<{ access_product_id: string; kind: string; trainer_slug?: string }> }>("/api/v1/public/storefront");
                const offer = store.offers.find((o) => o.trainer_slug === slug && o.kind === "one_time");
                const res = await api<SessionPayload>("/api/v1/learning/sessions", {
                  method: "POST",
                  body: JSON.stringify({ trainerSlug: slug, restart: true, confirmTrial: true, accessProductId: offer?.access_product_id }),
                });
                setSessionId(res.id);
                setItems(res.items);
                setAnswerKeys(res.answerKeys ?? {});
                setI(0);
                setAskResume(false);
                clearLastStep(slug);
                await saveLessonSnapshot({
                  slug,
                  sessionId: res.id,
                  learnerKey: "active",
                  items: res.items,
                  answerKeys: res.answerKeys,
                  startedAt: Date.now(),
                  entitlementExpiresAt: null,
                  lastIndex: 0,
                });
              }}
            >
              Начать заново
            </button>
          </div>
          <p className="muted">Новая попытка не удалит историю предыдущей.</p>
        </div>
      ) : null}
      {!done && item && !askResume ? (
        <div key={item.id}>
          {slug === "word-in-frame" ? (
            <FramePlayer item={item} onSubmit={(s) => void submit(s)} />
          ) : slug === "phrase-director" ? (
            <PhrasePlayer item={item} onSubmit={(s) => void submit(s)} />
          ) : (
            <SoundsPlayer item={item} onSubmit={(s) => void submit(s)} />
          )}
        </div>
      ) : null}
      {done ? (
        <div>
          <p>Сегодня потренировали {meta?.title ?? "навык"}.</p>
          {preview ? <a className="btn" href={`/start/${slug}`}>Начать 7 дней</a> : <a className="btn" href="/app">На главную</a>}
        </div>
      ) : null}
      {msg ? <p>{msg}</p> : null}
      {extra}
    </div>
    </PocketFrame>
  );
}
